import {
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  TextField,
  DialogActions,
  Stack,
  Typography,
  Link,
  Autocomplete,
  Checkbox,
  Box,
  Grid,
} from '@mui/material'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import { useAppSelector } from 'redux/Hook'
import {
  ENGAGEMENT_LEVEL_VALUES,
  FB_FILTER_LIST as filters,
} from 'lib/participants/Constants'
import { addFilterWithValue, updateFilter } from 'redux/slices/FilterBuilder'
import CustomChip from 'components/Shared/Chip'
import {
  projectActivityInitialState,
  getMappedProjectActivityFilters,
  isOptionEqualToValue,
  getEngagementText,
} from 'components/Participants/Filters/Project/Helper'
import {
  DialogActionsStyles,
  DialogContentColorHex,
  DialogTitleStyles,
} from 'components/Participants/Filters/Project/StyleValues'
import { isFrenchLocale } from 'lib/utility/LocaleHelper'
import { dialogActionsStyle as PRMFiltersDialogActionsStyle } from 'components/Participants/Filters/Styles'

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />

type ProjectActivityDialogProps = {
  open: boolean
  onClose: () => void
}

const FilterByProjectDialog = (props: ProjectActivityDialogProps) => {
  const { open: openModalInitialState, onClose } = props

  // keep track of the local state of projects
  const [projectActivityLocalState, setProjectActivityLocalState] =
    React.useState(projectActivityInitialState)
  const FILTER_BY_PROJECT_ACTIVITY = 'projects'

  const dispatch = useDispatch()
  const projects = useAppSelector((state) => state.projects.data)

  const { t } = useTranslation()
  const { selectedFilters } = useAppSelector((state) => state.filterBuilder)
  const filterByProjectAndEngagement = filters.find(
    (filter) => filter.name === FILTER_BY_PROJECT_ACTIVITY,
  )

  // find will give us the first item that matches the condition hence changing it to use the filter method
  const selectedProjectAndEngagementFilters = selectedFilters.filter(
    (filter) => filter.name === FILTER_BY_PROJECT_ACTIVITY,
  )

  // check if the filter is selected
  const isFilterByProjectAndEngagementSelected =
    selectedProjectAndEngagementFilters.length !== 0

  React.useEffect(() => {
    // setting the local state if the pill has already been added
    if (isFilterByProjectAndEngagementSelected) {
      for (let i = 0; i < selectedProjectAndEngagementFilters.length; i++) {
        // set the local state based on the global state value and condition
        const item: IndexType = selectedProjectAndEngagementFilters[i]
        setProjectActivityLocalState((prevState: any) => {
          // after setting the pill and navigating to a details page was causing
          // the state reset, hence using prevState logic https://reactjs.org/docs/hooks-reference.html#functional-updates
          const { condition, value } = item
          return {
            ...prevState,
            [condition]: {
              condition: condition,
              value: value,
            },
          }
        })
      }
    } else {
      // set the initial state
      setProjectActivityLocalState(projectActivityInitialState)
    }
  }, [openModalInitialState])

  const handleClose = () => {
    setProjectActivityLocalState(projectActivityInitialState)
    // trigger state change which in turns closes the modal
    onClose()
  }

  const getSelectedProjects = (condition: string | number) => {
    // now get the project based on the condition
    const projectActivityValue = projectActivityLocalState[condition]

    if (projectActivityValue && projectActivityValue.value.length !== 0) {
      return projectActivityValue.value.split(',').map((id: string) => {
        return { name: getProjectName(id), id }
      })
    }

    return []
  }

  const getProjectName = (id: string) => {
    const filteredProject = projects.find((project) => project.id == id) as any
    return filteredProject?.name
  }

  const handleProjectChange = (condition: Condition, value: Tags) => {
    const projectString = value.map((project) => project.id).toString()
    setProjectActivityLocalState({
      ...projectActivityLocalState,
      [condition]: {
        condition,
        value: projectString,
      },
    })
  }

  const handleApply = () => {
    /**
     * projectActivity Localstate will contain the
     * project ids segeregated based on the engagement activity
     *
     * getMappedProjectActivityFilters(projectActivityLocalState) => gives
     * an array of object which will have the value of the localState mapped
     * with its specific activity and the condition , that has to be prepped for
     * the segment call when the handleApply() function gets completed.
     */

    const mappedFilters = getMappedProjectActivityFilters(
      projectActivityLocalState,
    )

    mappedFilters.forEach((filter) => {
      const existingFilterIndex = selectedFilters.findIndex(
        (selectedFilter) =>
          selectedFilter.name === filter.name &&
          selectedFilter.condition === filter.condition,
      )

      if (existingFilterIndex === -1) {
        // adds the new selected filter as a pill - high level
        /**
         * In the background 👇
         *
         * create a new object and adds it to the selected Filter global state which
         * will then reTrigger the FilterBuilder causing to render the Pills
         *
         * selectedFilter ( state update ) => segment call => updates the grid
         */
        dispatch(
          addFilterWithValue({ ...filterByProjectAndEngagement, ...filter }),
        )
      } else {
        // update the filter
        dispatch(
          updateFilter({
            ...filterByProjectAndEngagement,
            item: filter,
            index: existingFilterIndex,
          }),
        )
      }
    })

    handleClose()
  }

  const helpdeskLang = isFrenchLocale() ? 'fr' : 'en'

  const renderAutocomplete = () => {
    return ENGAGEMENT_LEVEL_VALUES.map(
      (engagementLevel: Condition, index: number) => {
        const engagementText = t(
          `participant.projectAndEngagementLevel.${getEngagementText(
            engagementLevel,
          )}`,
        )
        return (
          <Grid item xs={12} key={index} display={'flex'}>
            <Stack
              direction={{ md: 'row', xs: 'column' }}
              alignItems={{ md: 'center', xs: 'stretch' }}
              flexBasis={'100%'}
              flexGrow={1}
            >
              <Box flexBasis={'100%'} flexGrow={1}>
                <Typography pt={2}>{engagementText}</Typography>
              </Box>

              <Box flexGrow={1} flexBasis={'100%'}>
                <Autocomplete
                  multiple
                  limitTags={1}
                  fullWidth
                  options={projects}
                  sx={{
                    width: {
                      md: 740,
                    },
                    flex: {
                      sm: 1,
                    },
                  }}
                  size="small"
                  openText={t('filters.ariaTexts.open')}
                  closeText={t('filters.ariaTexts.close')}
                  clearText={t('filters.ariaTexts.clear')}
                  isOptionEqualToValue={isOptionEqualToValue}
                  value={getSelectedProjects(engagementLevel)}
                  getOptionLabel={(option) => option.name}
                  onChange={(event: any, value: any) =>
                    handleProjectChange(engagementLevel, value)
                  }
                  noOptionsText={t('common.noOptions')}
                  renderTags={(tagValue, getTagProps: any) => {
                    return tagValue.map((option, index) => {
                      return (
                        <CustomChip
                          key={option.name}
                          filterValues={[option.name]}
                          size="medium"
                          extraProps={{
                            ...getTagProps({ index }),
                            color: 'primary',
                          }}
                        />
                      )
                    })
                  }}
                  renderOption={(props, option: IndexType, { selected }) => (
                    <li {...props} key={option.id}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        checked={selected}
                      />
                      {option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('filters.projects.label')}
                      placeholder={t('filters.search')}
                      variant="standard"
                    />
                  )}
                />
              </Box>
            </Stack>
          </Grid>
        )
      },
    )
  }

  return (
    <div>
      <Dialog open={openModalInitialState} onClose={handleClose} maxWidth="md">
        <Box sx={DialogTitleStyles}>
          <Typography variant="h5" component="h5">
            {t('participant.projectAndEngagementLevel.dialogTitle')}
          </Typography>
        </Box>
        <DialogContent>
          <DialogContentText color="rgba(0, 0, 0, 0.87)">
            {t('participant.projectAndEngagementLevel.dialogContent')}
          </DialogContentText>
          <Stack paddingTop={4} direction="row" alignItems="center" spacing={2}>
            <Typography
              variant="h6"
              component="h6"
              color={DialogContentColorHex}
            >
              {t('participant.projectAndEngagementLevel.dialogContentHeading')}
            </Typography>
            <Link
              underline="none"
              target="_blank"
              href={`https://helpdesk.bangthetable.com/${helpdeskLang}/articles/402959-what-does-aware-informed-and-engaged-mean`}
            >
              {t('participant.projectAndEngagementLevel.dialogLink')}
            </Link>
          </Stack>

          <Grid rowSpacing={3} columnSpacing={2} container mt={0}>
            {renderAutocomplete()}
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{ ...DialogActionsStyles, ...PRMFiltersDialogActionsStyle }}
        >
          <Button onClick={handleClose}>{t('common.cancel')}</Button>
          <Button onClick={handleApply} variant="contained">
            {t('participant.projectAndEngagementLevel.applyBtnText')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default FilterByProjectDialog
